<template>
  <div>
    <v-list dense class="pa-0 mx-0">
      <div v-if="filterToggle == filterToggles.selected && selectionCount == 0" class="pa-4 text-xs-center">
        <v-icon color="warning">warning</v-icon><br />
        No stations selected
      </div>
      <VirtualList v-show="!(filterToggle == filterToggles.selected && selectionCount == 0)" ref="virtualScroll" :size="40" :remain="6">
        <template v-for="(item, index) in combinedItems">
          <template v-if="item.type == 0">
            <v-list-tile :key="`category-${index}`" @click="toggleExpand(item)">
              <v-list-tile-action style="min-width: 0px;">
                <v-checkbox color="primary" :input-value="isSelected(item)" @click.stop="toggleCategory(item)" hide-details :indeterminate="isPartialSelection(item)" />
              </v-list-tile-action>
              <v-list-tile-avatar class="pl-0" style="min-width: 0px;">
                <v-icon>{{ item.icon || getIcon(item.name) }}</v-icon>
              </v-list-tile-avatar>
              <v-list-tile-content style="flex-grow: 1" v-text="item.name" />
              <v-list-tile-content class="font-weight-light grey--text text--darken-0" style="flex-grow: 0;">
                <v-chip color="rgba(0,0,0,.07)" small>
                  <span :class="{ 'error--text': item.count == 0}">
                    {{ item.count }}
                  </span>
                </v-chip>
              </v-list-tile-content>
              <v-list-tile-action class="ml-2" style="min-width: 0;">
                <v-icon>{{ item.open || isSearching ? 'arrow_drop_up' : 'arrow_drop_down'}}</v-icon>
              </v-list-tile-action>
            </v-list-tile>
          </template>
          <template v-else>
            <v-list-tile :key="`item-${index}`" @click="toggleStation(item.id)">
              <v-list-tile-avatar style="min-width: 0px;"></v-list-tile-avatar>
              <v-list-tile-action style="min-width: 0px;">
                <v-checkbox @click.prevent primary color="primary" :input-value="getStationValue(item)" hide-details />
              </v-list-tile-action>
              <v-list-tile-content>
                <v-list-tile-title class="font-weight-thin">{{ item.name }}
                </v-list-tile-title>
                <v-list-tile-sub-title v-html="item.group"></v-list-tile-sub-title>
              </v-list-tile-content>
            </v-list-tile>
          </template>
        </template>
      </VirtualList>
    </v-list>
  </div>
</template>

<script>
  import virtualList from 'vue-virtual-scroll-list'

  import {
    groupTypes,
  } from '@/store/stations/'

  import {
    debounce,
  } from '@/helpers/performance'

  import {
    stringSorter,
  } from '@/helpers/sorting'

  const defaultDebounceDelay = 500

  const queryTypes = {
    category: 0,
    station: 1,
  }

  export default {
    components: {
      VirtualList: virtualList,
    },
    props: {
      filterToggle: Number,
      filterToggles: Object,
      selectionCount: Number,
      search: String,
      stations: Array,
      missingStations: Array,
    },
    data() {
      return {
        initialized: false,
        groupTypes,
        queryTypes,
        loading: {
          items: false,
        },
        searching: false,
        selected: {},
        menuProps: {
          closeOnContentClick: false,
        },
        menu: {
          show: false,
        },
        stationCategories: {},
      }
    },
    computed: {
      _missingStations: {
        get() {
          return this.missingStations
        },
        set(stations) {
          this.$emit('update:missingStations', stations)
        },
      },
      combinedItems() {
        let items = []

        if (this.filterToggle == this.filterToggles.selected) {
          const selectedCategory = {
            ignore: true,
            name: 'Various',
            icon: 'help',
            open: true,
            count: this.selectionCount,
            type: this.queryTypes.category,
          }

          items.push(selectedCategory)
          this.sortedCategoryKeys.forEach(key => {
            let category = this.stationCategories[key]

            category.shownStations.forEach(station => {
              if (this.selected[key][station.id]) items.push(station)
            })
          })
        } else {
          this.sortedCategoryKeys.forEach(key => {
            let category = this.stationCategories[key]
            if (category.shownStations.length === 0) return

            items.push(category)
            if (category.open || this.isSearching) {
              items.push(...category.shownStations)
            }
          })
        }

        return items
      },
      sortedCategoryKeys() {
        return Object.keys(this.stationCategories).sort(stringSorter)
      },
      filteredStationCategories() {
        let result = {}
        Object.entries(this.stationCategories).forEach(([key, category]) => {
          if (category.show) result[key] = category
        })

        return result
      },
      categoryIcons() {
        let result = {}
        let stationCategories = this.$store.getters['stations/stationCategories']

        if (!stationCategories) return result

        stationCategories.forEach(sc => {
          result[sc.Name] = sc
        })

        return result
      },
      isSearching() {
        return this.search && !this.loading.items
      },
      queryResults() {
        return debounce(function(value) {
          this.filteredItems = this.updateItems(value)
          this.searching = false
          this.menu.show = true
        }, defaultDebounceDelay)
      },
    },
    watch: {
      filterToggle() {
        this.forceRender()
      },
      stations: {
        immediate: true,
        handler(value) {
          this.makeStationCategories(value)
          this.queryResults('')
        },
      },
      stationCategories(val, oldValue) {
        if (Object.keys(oldValue).length === 0 && Object.keys(val).length !== 0) {
          this.$nextTick(() => this.updateItems())
        }
      },
      searching(value) {
        this.$emit('isSearching', value)
      },
      search(value) {
        this.searching = true
        this.queryResults(value)
      },
      selected: {
        deep: true,
        handler: function(value) {
          this.$emit('selected', value)
        },
      },
    },
    methods: {
      makeStationCategories(stations) {
        if (!stations) return

        this.initialized = true
        let categories = {}

        stations.forEach(station => {
          if (!categories[station.category]) {
            categories[station.category] = {
              name: station.category,
              normalizedName: station.category.toLowerCase(),
              type: queryTypes.category,
              stations: [],
              shownStations: [],
              open: false,
              show: true,
              count: 0,
            }
          }

          categories[station.category].stations.push(station)
        })

        this.stationCategories = categories
        let selected = {}
        Object.keys(categories).forEach(key => selected[key] = {})
        this.selected = selected
      },
      getIcon(categoryName) {
        let category = this.categoryIcons[categoryName]
        if (!category) return 'help'
        return category.IconPath
      },
      isPartialSelection(category) {
        if (category.ignore) return false

        let selection = this.selected[category.name]
        let keyLength = Object.keys(selection).length
        return keyLength > 0 && keyLength != this.stationCategories[category.name].count
      },
      isSelected(category) {
        if (category.ignore) return true

        let selection = this.selected[category.name]
        let keyLength = Object.keys(selection).length
        return keyLength > 0
      },
      toggleExpand(category) {
        category.open = !category.open

        if (!this.search) {
          this.updateItems(this.search)
        }
      },
      updateItems(searchValue) {
        if (!this.stationCategories) return

        const normalizedValue = searchValue && searchValue.toLowerCase() || null
        Object.keys(this.stationCategories).forEach(key => {
          let category = this.stationCategories[key]

          const matchesCategory = category.normalizedName.includes(normalizedValue)
          if (normalizedValue && !matchesCategory) {
            category.shownStations = category.stations.filter(station =>
              station.normalizedName.includes(normalizedValue))
            category.show = category.shownStations.length > 0
            category.count = category.shownStations.length
          } else {
            category.show = true
            category.shownStations = category.stations
          }

          category.count = category.shownStations.length
        })

        this.loading = {
          ...this.loading,
          items: false,
        }
      },
      toggleCategory(category) {
        if (category.ignore) {
          this.untoggleAll()
          return
        }

        let selected = this.selected[category.name]
        if (Object.keys(selected).length < category.count) {
          category.shownStations.forEach(item => {
            this.$set(this.selected[category.name], item.id, item)
          })
        } else {
          category.stations.forEach(item => {
            this.$delete(selected, item.id)
          })
        }
      },
      makeEmptySelection() {
        let selection = {}
        Object.keys(this.selected).forEach(category => {
          selection[category] = {}
        })

        return selection
      },
      untoggleAll() {
        if (this.selectionCount == 0) return
        let selection = this.makeEmptySelection()
        this.selected = selection
      },
      toggleStation(id) {
        let item = this.stations.find(station => station.id == id)
        if (this.selected[item.category][item.id]) {
          this.$delete(this.selected[item.category], item.id)
        } else {
          this.$set(this.selected[item.category], item.id, item)
        }
      },
      async selectStationIds(stationIds) {
        if (!stationIds) {
          this.untoggleAll()
          return
        }

        let selected = this.makeEmptySelection()
        let missingStationIds = []
        stationIds.forEach(id => {
          let station = this.stations.find(s => s.id == id)
          if (!station) {
            missingStationIds.push(id)
          } else {
            selected[station.category][station.id] = station
          }
        })

        // Trigger DOM update all at once
        this.selected = selected

        this._missingStations = missingStationIds

        await this.$nextTick()
        this.forceRender()
      },
      forceRender() {
        this.$refs.virtualScroll.forceRender()
      },
      getStationValue(item) {
        return !!this.selected[item.category][item.id]
      },
    },
  }
</script>